/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Paper,
  Typography,
  Grid,
  Chip,
  Box,
  Divider,
  Stack,
  Button,
  Alert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { getNestedValue, isFieldVisible } from './schemaUtils';
import RequestHistoryTimeline from './RequestHistoryTimeline';
import RequestThread from './RequestThread';
import RequestChangeLog from './RequestChangeLog';
import WaterScheduleFields from '../WaterScheduleFields';
import { describeDays, isEmptyScheduleEntry } from '../../../utils/waterSchedule';
import {
  requestStatusColor,
  requestStatusLabel
} from '../../../utils/requestStatus';

const formatValue = (field, value) => {
  if (value === undefined || value === null || value === '') return <em>—</em>;
  if (field.type === 'switch' || field.type === 'checkbox') {
    return value ? 'Yes' : 'No';
  }
  if (field.type === 'select' || field.type === 'radio') {
    const opt = (field.options || []).find(o => o.value === value);
    return opt?.label || String(value);
  }
  if (field.type === 'chamberAssignmentSelect') {
    if (!value || typeof value !== 'object') return <em>—</em>;
    const start = value.start_date ? new Date(value.start_date).toLocaleDateString() : '';
    // A compartment-scoped pick has no single rate or assignment to show.
    if (value.scope === 'chamber') {
      const parts = [value.chamber_name || 'Compartment'];
      if (value.quantity) parts.push(`${value.quantity} ${value.unit_short || 'units'}`);
      if (value.assignment_count > 1) parts.push(`${value.assignment_count} assignments`);
      if (start) parts.push(`since ${start}`);
      return parts.join(' · ');
    }
    return `${value.chamber_name || 'Compartment'} · ${value.rate_id || ''} · ${value.quantity || 0} ${value.unit_short || 'units'}${start ? ` · since ${start}` : ''}`;
  }
  if (field.type === 'datetimeAmPm') {
    if (!value || typeof value !== 'object' || !value.date) return <em>—</em>;
    const dateStr = (() => {
      try { return new Date(value.date).toLocaleDateString(); } catch { return value.date; }
    })();
    const time = (value.hour && value.minute && value.period)
      ? `${value.hour}:${String(value.minute).padStart(2, '0')} ${value.period}`
      : '';
    return [dateStr, time].filter(Boolean).join(' · ');
  }
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

/** Read-only rendering of a requested watering schedule. */
const WaterScheduleView = ({ field, entries }) => {
  const list = (Array.isArray(entries) ? entries : []).filter(e => !isEmptyScheduleEntry(e));
  if (list.length === 0) {
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{field.label}</Typography>
        <Typography variant="body2" color="text.secondary">Not requested</Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>{field.label}</Typography>
      <Stack spacing={1.5}>
        {list.map((entry, i) => (
          <Paper key={entry.water || i} variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {entry.water_label || 'Water'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {describeDays(entry.week1, entry.week2)}
            </Typography>
            <WaterScheduleFields entry={entry} onChange={() => {}} readOnly />
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

const ViewSection = ({ section, formData }) => (
  <Box sx={{ mb: 3 }}>
    {section.title && (
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        {section.title}
      </Typography>
    )}
    <Grid container spacing={1}>
      {section.fields.map(field => {
        if (!isFieldVisible(field, formData)) return null;
        const value = getNestedValue(formData, field.name);
        if (field.type === 'waterScheduleBuilder') {
          return (
            <Grid item xs={12} key={field.name}>
              <WaterScheduleView field={field} entries={value} />
            </Grid>
          );
        }
        if (field.type === 'repeater') {
          const items = Array.isArray(value) ? value : [];
          return (
            <Grid item xs={12} key={field.name}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{field.label}</Typography>
              {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">—</Typography>
              ) : (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {items.map((item, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                      <Grid container spacing={1}>
                        {(field.fields || []).map(sub => (
                          <Grid item xs={12} sm={6} key={sub.name}>
                            <Typography variant="caption" color="text.secondary">{sub.label}</Typography>
                            <Typography variant="body2">{formatValue(sub, getNestedValue(item, sub.name))}</Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Grid>
          );
        }
        return (
          <Grid item xs={12} sm={6} key={field.name}>
            <Typography variant="caption" color="text.secondary">{field.label}</Typography>
            <Typography variant="body2">{formatValue(field, value)}</Typography>
          </Grid>
        );
      })}
    </Grid>
  </Box>
);

const StatusChip = ({ status }) => (
  <Chip size="small" label={requestStatusLabel(status)} color={requestStatusColor(status)} />
);

const downloadFromUrl = async (url, filename) => {
  try {
    const res = await axios.get(url, { responseType: 'arraybuffer' });
    const blob = new Blob([res.data], { type: res.headers['content-type'] });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename || 'attachment';
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (err) {
    console.error('Error downloading attachment:', err);
  }
};

const downloadAttachment = (request, response, attachment) =>
  downloadFromUrl(
    `/api/requests/${request._id}/responses/${response._id}/attachments/${attachment._id}`,
    attachment.filename
  );

const downloadRequestAttachment = (request, attachment) =>
  downloadFromUrl(
    `/api/requests/${request._id}/attachments/${attachment._id}`,
    attachment.filename
  );

const DynamicRequestView = ({ request, showHistory = true, isAdmin = false, onChanged }) => {
  const [schema, setSchema] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const key = request?.request_type_key;
        if (!key) return;
        const facilityId = request?.facility?._id || request?.facility;
        const url = facilityId
          ? `/api/requests/schemas/${key}?facilityId=${facilityId}`
          : `/api/requests/schemas/${key}`;
        const { data } = await axios.get(url);
        if (!cancelled) setSchema(data);
      } catch (err) {
        console.error('Error loading schema for view:', err);
        if (!cancelled) setError('Failed to load form layout');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [request]);

  if (!request) return null;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">{request.request_type}</Typography>
            <Typography variant="body2" color="text.secondary">
              {request.request_id}{request.facility_name ? ` · ${request.facility_name}` : ''}
            </Typography>
          </Box>
          <StatusChip status={request.status} />
        </Stack>
        <Divider sx={{ my: 1.5 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Project</Typography>
            <Typography variant="body2">{request.project_title || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Submitted</Typography>
            <Typography variant="body2">{new Date(request.submitted_at).toLocaleString()}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">PI</Typography>
            <Typography variant="body2">{request.pi_name || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">PI Email</Typography>
            <Typography variant="body2">{request.pi_email || '—'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Submitted Form
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!schema && !error && (
          <Typography variant="body2" color="text.secondary">Loading form layout…</Typography>
        )}
        {schema && (
          <Box>
            {schema.sections.map((section, idx) => (
              <ViewSection key={idx} section={section} formData={request.form_data || {}} />
            ))}
          </Box>
        )}
      </Paper>

      {(request.attachments || []).length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Attached Files
          </Typography>
          <Stack spacing={1}>
            {(request.attachments || []).map((att) => (
              <Stack key={att._id} direction="row" alignItems="center" spacing={1}>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadRequestAttachment(request, att)}
                  sx={{ textTransform: 'none' }}
                >
                  {att.filename}
                </Button>
                {att.uploaded_at && (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(att.uploaded_at).toLocaleString()}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* One conversation with the facility for the life of the request. It is
          what replaced the revision cycle: ask or answer here, then edit the
          form, and the edit is recorded below. */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Request Thread
        </Typography>
        <RequestThread request={request} isAdmin={isAdmin} onChanged={onChanged} />
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Form Changes ({(request.form_changes || []).length})
        </Typography>
        <RequestChangeLog request={request} dense />
      </Paper>

      {(request.admin_responses || []).filter(r => r.attachments?.length).length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Admin Attachments
          </Typography>
          <Stack spacing={1}>
            {(request.admin_responses || []).map((resp) => (
              (resp.attachments || []).map((att) => (
                <Stack key={att._id} direction="row" alignItems="center" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadAttachment(request, resp, att)}
                  >
                    {att.filename}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(resp.responded_at).toLocaleString()}
                  </Typography>
                </Stack>
              ))
            ))}
          </Stack>
        </Paper>
      )}

      {showHistory && (
        <RequestHistoryTimeline request={request} isAdmin={isAdmin} />
      )}
    </Box>
  );
};

export default DynamicRequestView;
